import { type ComponentProps, useEffect, useState } from 'react';
import { sprintf } from 'sprintf-js';

import { Box } from 'components/Box/Box';
import { FilePickerField } from 'components/Form/FileUploadField';
import { InputField } from 'components/Form/InputField';
import { MultiInputField } from 'components/Form/MultiInputField';
import { RecaptchaField } from 'components/Form/RecaptchaField';
import { SelectInputField } from 'components/Form/SelectInputField';
import { SubmitButton } from 'components/Form/SubmitButton/SubmitButton';
import { TextAreaField } from 'components/Form/TextAreaField';
import { useUser } from 'contexts/UserEnvironmentContext';
import { useI18n } from 'i18n/hooks/useI18n';
import type {
  CmsApiSupportContactFormBlockReason,
  CmsApiSupportContactFormBlockSubReason,
} from 'modules/cms/api/types/blocks/CmsApiSupportContactFormBlock';
import { LOCALE_RADIO_OR_SELECT_OPTIONS } from 'utils/constants/general/locales';

const ATTACHMENTS_MAX_LENGTH = 5;

type Props = {
  reasons: CmsApiSupportContactFormBlockReason[];
  selectedReason: CmsApiSupportContactFormBlockReason | null | undefined;
  selectedSubReason: CmsApiSupportContactFormBlockSubReason | null | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateValue: (...args: any[]) => any;
};

export function CmsSupportContactFormBlockContactForm({
  reasons,
  selectedReason,
  selectedSubReason,
  updateValue,
}: Props) {
  const { i18n } = useI18n();

  const { user } = useUser();
  const [subReasons, setSubReasons] = useState<
    CmsApiSupportContactFormBlockSubReason[]
  >([]);
  const [subSubReasons, setSubSubReasons] = useState<
    CmsApiSupportContactFormBlockSubReason[]
  >([]);
  useEffect(() => {
    if (selectedReason) {
      setSubReasons(selectedReason.subReasons || []);

      if (selectedReason.id === 'ORG' && selectedSubReason) {
        setSubSubReasons(selectedSubReason.subReasons ?? []);
      } else {
        setSubSubReasons([]);
        updateValue('subSubReason', undefined);
      }
    }
  }, [selectedReason, selectedSubReason, updateValue]);
  return (
    <>
      <InputField label={`${i18n('Email address')}*`} name="email" />
      <InputField label={`${i18n('First name')}*`} name="firstName" />
      <InputField label={`${i18n('Last name')}*`} name="lastName" />
      <SelectInputField
        label={`${i18n('Your language')}*`}
        name="locale"
        options={LOCALE_RADIO_OR_SELECT_OPTIONS()}
      />
      <SelectInputField
        label={`${i18n('What is this about?')}*`}
        name="reason"
        options={[
          {
            label: `${i18n('Please Select')}`,
            value: '',
          },
        ].concat(
          reasons.map((reason) => ({
            label: reason.text,
            value: reason.id,
          })),
        )}
      />
      {subReasons.length > 0 && (
        <SelectInputField
          label={`${i18n('Narrow it down')}*`}
          name="subReason"
          options={[
            {
              label: `${i18n('Please select')}`,
              value: '',
            },
          ].concat(
            subReasons.map((subReason) => ({
              label: subReason.text,
              value: subReason.id,
            })),
          )}
        />
      )}
      {subSubReasons.length > 0 && (
        <SelectInputField
          label={`${i18n('Narrow it down')}*`}
          name="subSubReason"
          options={[
            {
              label: `${i18n('Please select')}`,
              value: '',
            },
          ].concat(
            subSubReasons.map((subSubReason) => ({
              label: subSubReason.text,
              value: subSubReason.id,
            })),
          )}
        />
      )}
      {user !== null && user.orgs.length > 0 ? (
        <SelectInputField
          label={`${i18n('Which organization is this about?')}*`}
          name="orgName"
          options={[
            {
              label: `${i18n('This is not about an organization')}`,
              value: '',
            },
          ].concat(
            user.orgs.map((org) => ({
              label: org.name,
              value: org.name,
            })),
          )}
        />
      ) : (
        <InputField
          label={`${i18n('Which organization is this regarding (if any)?')}`}
          name="orgName"
        />
      )}
      <TextAreaField
        label={
          selectedReason && selectedReason.id === 'FEEDBACK'
            ? i18n('Please describe your feedback in detail')
            : i18n('Tell us more!')
        }
        name="message"
      />
      <MultiInputField
        hideControls
        component={FilePickerField}
        maxInputs={ATTACHMENTS_MAX_LENGTH}
        name="attachmentIds"
        label={i18n('Attachments')}
        description={sprintf(getText('Upload up to %(count)s attachments'), {
          count: ATTACHMENTS_MAX_LENGTH,
        })}
        extraData={Array.from<Partial<ComponentProps<typeof FilePickerField>>>({
          length: ATTACHMENTS_MAX_LENGTH,
        }).fill({
          downloadAttachment: false,
          fileType: 'CONTACT_FORM',
          label: '',
          extraAnalyticsData: {
            context: 'Support Contact Form',
            file_label: 'Attachments',
          },
        })}
        showEmpty
      />
      <RecaptchaField />
      <Box mt={64} display="flex" justifyContent="flex-end">
        <SubmitButton size="large">{i18n('Submit')}</SubmitButton>
      </Box>
    </>
  );
}
